import { useState, useEffect } from 'react'

const EMPTY_ADMIN_DEFAULTS = {}

/**
 * Generic hook for initializing group members from user info.
 * Consolidates the initialization logic used by both AlertSets and HeatStressGroupMembers.
 *
 * @param {Object} params
 * @param {boolean} params.userChecked - Whether user data has been loaded
 * @param {Object} params.info - User info object
 * @param {string} params.propertyName - Property name in info object ('alertSetGroupMembers' or 'heatStressGroupMembers')
 * @param {string} params.adminLabel - Label for admin member ('Group Admin (You)' or 'Admin (You)')
 * @param {Object} params.adminDefaults - Additional defaults for admin member (e.g., { alertPrefs: [] })
 * @returns {Object} { groupMembers, setGroupMembers }
 */
export function useGroupMemberInit ({
  userChecked,
  info,
  propertyName,
  adminLabel,
  adminDefaults = EMPTY_ADMIN_DEFAULTS
}) {
  const [groupMembers, setGroupMembers] = useState([])

  useEffect(() => {
    if (userChecked && info) {
      const savedMembers = (info[propertyName] || []).filter(m => m != null)
      const hasAdmin = savedMembers.some(m => m._id === info._id)

      const adminMember = {
        _id: info._id,
        name: adminLabel,
        email: info.email,
        phone: info.phone,
        admin: true,
        ...adminDefaults
      }

      const rawMembers = hasAdmin ? savedMembers : [adminMember, ...savedMembers]

      const gm = rawMembers.map(m => {
        if (m._id === info._id) {
          // Admin member: always sync email/phone from user info
          return {
            ...m,
            email: info.email,
            phone: info.phone,
            admin: true
          }
        }
        // Non-admin members: preserve verification status
        return {
          ...m,
          emailVerified: m.emailVerified !== undefined ? m.emailVerified : false,
          phoneVerified: m.phoneVerified !== undefined ? m.phoneVerified : false
        }
      })
      setGroupMembers(gm)
    }
  }, [userChecked, info, propertyName, adminLabel, adminDefaults])

  return { groupMembers, setGroupMembers }
}
